import { useState } from 'react';
import { incidentsApi, type IncidentSeverity } from '../../services/incidentsApi';

type NewIncidentDraft = {
  title: string;
  description: string;
  severity: IncidentSeverity;
  incident_type: string;
  zone: string;
};

type UseIncidentsPageActionsArgs = {
  fetchData: () => Promise<void>;
  setError: (value: string | null) => void;
  setCreateDialogOpen: (value: boolean) => void;
};

const EMPTY_NEW_INCIDENT: NewIncidentDraft = {
  title: '',
  description: '',
  severity: 'medium',
  incident_type: 'safety',
  zone: '',
};

export function useIncidentsPageActions({
  fetchData,
  setError,
  setCreateDialogOpen,
}: UseIncidentsPageActionsArgs) {
  const [newIncident, setNewIncident] = useState<NewIncidentDraft>(EMPTY_NEW_INCIDENT);

  const handleCreateIncident = async () => {
    try {
      await incidentsApi.createIncident(newIncident);
      setCreateDialogOpen(false);
      setNewIncident(EMPTY_NEW_INCIDENT);
      await fetchData();
    } catch (error: any) {
      setError(error?.response?.data?.error || 'Failed to create incident');
    }
  };

  const handleSyncFromOdoo = async (incidentId: string) => {
    try {
      await incidentsApi.syncFromOdoo(incidentId);
      await fetchData();
    } catch (error: any) {
      setError(error?.response?.data?.error || 'Failed to sync from Odoo');
    }
  };

  const handleEnsureOdooTicket = async (incidentId: string) => {
    try {
      const response = await incidentsApi.ensureOdooTicket(incidentId);
      if (!response?.ok) {
        setError((response as any)?.error || 'Failed to create Odoo ticket');
      } else {
        setError(null);
      }
      await fetchData();
    } catch (error: any) {
      setError(error?.response?.data?.error || 'Failed to create Odoo ticket');
    }
  };

  const handleSyncAllFromOdoo = async () => {
    try {
      const result = await incidentsApi.syncAllFromOdoo();
      setError(null);
      await fetchData();
      window.alert(`Synced ${result.synced} of ${result.total} incidents from Odoo`);
    } catch (error: any) {
      setError(error?.response?.data?.error || 'Failed to bulk sync from Odoo');
    }
  };

  return {
    newIncident,
    setNewIncident,
    handleCreateIncident,
    handleSyncFromOdoo,
    handleEnsureOdooTicket,
    handleSyncAllFromOdoo,
  };
}
